import React, { useState } from "react";
import { Text, Flex, Box, Heading, Button } from "theme-ui";
import { useWeb3React } from "@web3-react/core";

import { LiquityStoreState } from "@liquity/lib-base";
import { useLiquitySelector } from "@liquity/lib-react";

import { COIN, GT } from "../strings";
import { useLiquity } from "../hooks/LiquityContext";
import { shortenAddress } from "../utils/shortenAddress";

import { Icon } from "./Icon";
import useChain from "../hooks/useChain";

const select = ({ accountBalance, usdsBalance, sableBalance }: LiquityStoreState) => ({
  accountBalance,
  usdsBalance,
  sableBalance
});

export const UserAccount: React.FC = () => {
  const { account } = useLiquity();
  const chain = useChain();
  const { deactivate } = useWeb3React();

  const { accountBalance, usdsBalance, sableBalance } = useLiquitySelector(select);
  const [showBalances, setShowBalances] = useState(false);

  const currencySymbol = chain.nativeCurrency.symbol;

  const handleDisconnect = () => {
    deactivate();
    setShowBalances(false);
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        position: "relative"
      }}
    >
      <Flex
        onMouseEnter={() => setShowBalances(true)}
        onMouseLeave={() => setShowBalances(false)}
        sx={{
          position: "relative",
          alignItems: "center",
          mr: 0,
          p: "8px 12px",
          minHeight: "40px",
          backgroundColor: "#fff",
          border: "1px solid #f0b90b",
          borderRadius: "8px",
          cursor: "pointer",
          transition: "all 0.2s ease",
          ":hover": {
            borderColor: "#d9a000",
            boxShadow: "0 2px 8px rgba(240, 185, 11, 0.2)"
          }
        }}
      >
        <Icon name="wallet" size="sm" />
        <Heading sx={{ ml: 2, fontSize: 1, fontWeight: "600", whiteSpace: "nowrap" }}>
          {shortenAddress(account)}
        </Heading>
      </Flex>

      {showBalances && (
        <Box
          onMouseEnter={() => setShowBalances(true)}
          onMouseLeave={() => setShowBalances(false)}
          sx={{
            position: "absolute",
            top: "100%",
            right: 0,
            backgroundColor: "white",
            border: "1px solid #E5E7EB",
            borderRadius: "12px",
            boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
            minWidth: "240px",
            zIndex: 1000,
            pt: "12px",
            px: 3,
            pb: 3,
            mt: "-4px",
            "@media screen and (max-width: 47.9375em)": {
              display: "none"
            }
          }}
        >
          <Flex sx={{ alignItems: "center", mb: 2, pb: 2, borderBottom: "1px solid #F3F4F6" }}>
            <Icon name="wallet" size="sm" />
            <Text
              sx={{
                ml: 2,
                fontSize: 0,
                fontWeight: "600",
                color: "#6B7280",
                textTransform: "uppercase",
                letterSpacing: "0.5px"
              }}
            >
              Wallet Balances
            </Text>
          </Flex>

          {(
            [
              [currencySymbol, accountBalance],
              [COIN, usdsBalance],
              [GT, sableBalance]
            ] as const
          ).map(([currency, balance], i) => (
            <Flex
              key={i}
              sx={{
                justifyContent: "space-between",
                alignItems: "center",
                py: 2,
                borderBottom: i < 2 ? "1px solid #F3F4F6" : "none"
              }}
            >
              <Text sx={{ fontSize: 1, fontWeight: "600", color: "#374151" }}>{currency}</Text>
              <Text sx={{ fontSize: 1, fontWeight: "500", color: "#6B7280" }}>
                {balance.prettify()}
              </Text>
            </Flex>
          ))}

          <Button
            onClick={handleDisconnect}
            sx={{
              mt: 3,
              pt: 3,
              borderTop: "1px solid #F3F4F6",
              width: "100%",
              bg: "#F0B90B",
              color: "#FFFFFF",
              fontSize: 1,
              fontWeight: "600",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 2,
              borderRadius: "8px",
              border: "none",
              transition: "all 0.2s",
              ":hover": {
                bg: "#D9A000",
                transform: "translateY(-1px)",
                boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)"
              }
            }}
          >
            Disconnect Wallet
          </Button>
        </Box>
      )}
    </Box>
  );
};
